/**
 * EFormsModal.tsx
 *
 * Patient-scoped E-Forms dispatcher. Mirrors the legacy load-eform-list.php
 * section of patient_details-test.php: pick a form and optionally a plan,
 * fill in any custom replacement codes, then display / email / text the form
 * short URL to the patient.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, Mail, MessageSquare } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui';
import { SearchableCombobox, type ComboboxOption } from '@/components/ui/SearchableCombobox';
import { formatPhone } from '@/lib/utils';
import { listSurveys, type Survey } from '@/services/surveys.service';
import { getEFormPlans, type EFormPlan } from '@/v5/services/notes.service';
import { getPatient, isEmailOptedOut, isSmsOptedOut } from '@/services/patients.service';
import { getErrorMessage } from '@/services/apiClient';
import { sendSMS, sendEmail } from '@/services/communications.service';
import {
  getEFormReplacementCodes,
  getEFormLink,
  getEmailTemplate,
  getSmsTemplate,
  replaceKeywords,
  type EFormLinkResponse,
} from '@/services/eforms.service';
import { useAuthStore } from '@/store/authStore';

interface EFormsModalProps {
  patientId: number;
  open: boolean;
  onClose: () => void;
}

// EFormPlan now includes displayId/lastContact/stageName/quoteCost/procedures
// directly (matches the V5 eformsForPatient response). Kept the alias so the
// rest of this file's existing references don't churn.
type EFormPlanWithExtras = EFormPlan;

interface FixedFormOption {
  value: string;
  label: string;
  accountScope?: number[];
}

const FIXED_FORM_OPTIONS: FixedFormOption[] = [
  { value: 'demographics', label: 'Demographics' },
  { value: 'history', label: 'Health History', accountScope: [1] },
  { value: 'financing', label: 'Financing', accountScope: [1, 6] },
];

/**
 * Legacy surveys-list convention: admins insert "spacer" surveys whose title
 * is a run of dashes surrounding a section name (e.g.
 * "------MEMBERSHIP AGREEMENTS------") to visually group the flat
 * <select> options. Detect those here so we can promote the section name
 * into a proper SearchableCombobox `group` header and skip rendering the
 * spacer itself as a pickable option. Also swallows pure-dash spacers that
 * have no label at all.
 */
const GROUP_SEPARATOR_REGEX = /^-{2,}\s*([^-][^]*?[^-]|[^-])\s*-{2,}$/;
const BARE_DASH_REGEX = /^-{2,}$/;

interface SeparatorInfo {
  isSeparator: boolean;
  groupName?: string;
}

function classifySurveyTitle(title: string): SeparatorInfo {
  const trimmed = title.trim();
  if (trimmed === '' || BARE_DASH_REGEX.test(trimmed)) {
    return { isSeparator: true };
  }
  const match = trimmed.match(GROUP_SEPARATOR_REGEX);
  if (match) {
    return { isSeparator: true, groupName: match[1].trim() };
  }
  return { isSeparator: false };
}

function formatShortDate(value: string | null | undefined): string {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

interface SectionProps {
  step: number;
  title: string;
  hint?: string;
  children: ReactNode;
}

function Section({ step, title, hint, children }: SectionProps) {
  return (
    <section className="space-y-2">
      <header>
        <h3 className="flex items-center text-sm font-semibold text-slate-900">
          <span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-800 text-[11px] font-semibold text-white">
            {step}
          </span>
          {title}
        </h3>
        {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
      </header>
      {children}
    </section>
  );
}

export function EFormsModal({ patientId, open, onClose }: EFormsModalProps) {
  const accountId = useAuthStore((s) => s.accountId);
  const practiceName = 'Next Health';

  const [surveyId, setSurveyId] = useState<string>('');
  const [planId, setPlanId] = useState<number>(0);
  const [customValues, setCustomValues] = useState<Record<string, string>>({});

  const [linkLoading, setLinkLoading] = useState(false);
  const [linkData, setLinkData] = useState<EFormLinkResponse | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  const [sendingSms, setSendingSms] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setSurveyId('');
      setPlanId(0);
      setCustomValues({});
      setLinkData(null);
      setLinkError(null);
      setFeedback(null);
      setCopied(false);
    }
  }, [open]);

  const { data: surveysPage, isLoading: surveysLoading } = useQuery({
    queryKey: ['eforms-surveys'],
    queryFn: () => listSurveys({ isActive: true, perPage: 500 }),
    enabled: open,
    staleTime: 60_000,
  });

  const { data: plans, isLoading: plansLoading } = useQuery({
    queryKey: ['eforms-plans', patientId],
    queryFn: () => getEFormPlans(patientId) as Promise<EFormPlanWithExtras[]>,
    enabled: open,
    staleTime: 30_000,
  });

  const { data: patient } = useQuery({
    queryKey: ['eforms-patient', patientId],
    queryFn: () => getPatient(patientId),
    enabled: open,
    staleTime: 60_000,
  });

  const isCustomSurvey = surveyId !== '' && /^\d+$/.test(surveyId);
  const numericSurveyId = isCustomSurvey ? Number(surveyId) : 0;

  const { data: replacementCodes } = useQuery({
    queryKey: ['eforms-replacement-codes', patientId, numericSurveyId],
    queryFn: () => getEFormReplacementCodes(patientId, numericSurveyId),
    enabled: open && numericSurveyId > 0,
    staleTime: 60_000,
  });

  const fixedOptions = useMemo(() => {
    return FIXED_FORM_OPTIONS.filter(
      (opt) => !opt.accountScope || (accountId !== null && opt.accountScope.includes(accountId)),
    );
  }, [accountId]);

  const surveys: Survey[] = surveysPage?.data ?? [];

  // Flatten fixed shortcuts + legacy "separator" surveys into groups so the
  // combobox can render section headers the way the native <select> used to.
  const comboOptions = useMemo<ComboboxOption<string>[]>(() => {
    const opts: ComboboxOption<string>[] = fixedOptions.map((opt) => ({
      value: opt.value,
      label: opt.label,
    }));
    let currentGroup: string | undefined;
    for (const s of surveys) {
      const info = classifySurveyTitle(s.title);
      if (info.isSeparator) {
        currentGroup = info.groupName;
        continue;
      }
      opts.push({
        value: String(s.id),
        label: s.title,
        group: currentGroup,
      });
    }
    return opts;
  }, [fixedOptions, surveys]);

  async function refreshLink(nextSurveyId: string, nextPlanId: number, nextCustom: Record<string, string>) {
    if (nextSurveyId === '') {
      setLinkData(null);
      setLinkError(null);
      return;
    }
    setLinkLoading(true);
    setLinkError(null);
    try {
      const isNumeric = /^\d+$/.test(nextSurveyId);
      const data = await getEFormLink(patientId, {
        surveyId: isNumeric ? Number(nextSurveyId) : 0,
        planId: nextPlanId || null,
        customCodes: isNumeric ? nextCustom : {},
      });
      setLinkData(data);
    } catch (err) {
      const msg = getErrorMessage(err, 'Failed to generate link');
      setLinkError(msg);
      setLinkData(null);
    } finally {
      setLinkLoading(false);
    }
  }

  function handleSurveyChange(value: string) {
    setSurveyId(value);
    setCustomValues({});
    setFeedback(null);
    void refreshLink(value, planId, {});
  }

  function handlePlanChange(value: number) {
    setPlanId(value);
    setFeedback(null);
    void refreshLink(surveyId, value, customValues);
  }

  function handleCustomChange(code: string, value: string) {
    const next = { ...customValues, [code]: value };
    setCustomValues(next);
    void refreshLink(surveyId, planId, next);
  }

  function handleDisplay() {
    if (!linkData) return;
    window.open(linkData.fullUrl, '_blank', 'noopener,noreferrer');
  }

  function handleCopy() {
    if (!linkData) return;
    void navigator.clipboard.writeText(linkData.shortUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  async function handleSendEmail() {
    if (!linkData || !patient || !patient.email) return;
    if (isEmailOptedOut(patient)) {
      setFeedback('This patient has opted out of transactional emails.');
      return;
    }
    if (!window.confirm('E-mail E-Form to this patient?')) return;

    setSendingEmail(true);
    setFeedback(null);
    try {
      let subject: string;
      let body: string;

      const selectedSurvey = isCustomSurvey ? surveys.find((s) => s.id === numericSurveyId) : null;
      const emailTemplateId = selectedSurvey?.emailContentId;

      if (emailTemplateId && emailTemplateId !== 0) {
        const template = await getEmailTemplate(emailTemplateId);
        subject = template.subject.replace(/\$\{FormUrl\}/g, linkData.shortUrl);
        body = template.body.replace(
          /\$\{FormUrl\}/g,
          `<a href="${linkData.shortUrl}">${linkData.shortUrl}</a>`,
        );
        [subject, body] = await Promise.all([
          replaceKeywords(patientId, subject, planId || null),
          replaceKeywords(patientId, body, planId || null),
        ]);
      } else {
        subject = `Electronic Form from ${practiceName}`;
        body = `You have been invited by ${practiceName} to complete a form at the following link:<br><a href="${linkData.shortUrl}">${linkData.shortUrl}</a>`;
      }

      await sendEmail({
        patientId,
        planId: planId || null,
        toEmail: patient.email,
        toName: `${patient.firstName ?? ''} ${patient.lastName ?? ''}`.trim() || null,
        subject,
        htmlContent: body,
      });
      setFeedback('E-form e-mailed.');
    } catch (err) {
      // getErrorMessage, not err.message: an AxiosError *is* an Error, so
      // err.message is "Request failed with status code 422" instead of the
      // server's reason (e.g. the transactional opt-out rejection).
      setFeedback(getErrorMessage(err, 'Failed to send e-mail.'));
    } finally {
      setSendingEmail(false);
    }
  }

  async function handleSendSms() {
    if (!linkData || !patient) return;
    const cell = patient.cellPhone;
    if (!cell) return;
    if (isSmsOptedOut(patient)) {
      setFeedback('This patient has opted out of transactional SMS messages.');
      return;
    }
    if (!window.confirm('SMS E-Form to this patient?')) return;

    setSendingSms(true);
    setFeedback(null);
    try {
      let message: string;

      const selectedSurvey = isCustomSurvey ? surveys.find((s) => s.id === numericSurveyId) : null;
      const smsTemplateId = selectedSurvey?.smsId;

      if (smsTemplateId && smsTemplateId !== 0) {
        const template = await getSmsTemplate(smsTemplateId);
        message = template.message.replace(/\$\{FormUrl\}/g, linkData.shortUrl);
        message = await replaceKeywords(patientId, message, planId || null);
      } else {
        message = `You have been invited by ${practiceName} to complete a form at the following link:\n${linkData.shortUrl}`;
      }

      await sendSMS({
        patientId,
        planId: planId || null,
        toPhone: cell,
        message,
      });
      setFeedback('E-form SMS sent.');
    } catch (err) {
      setFeedback(getErrorMessage(err, 'Failed to send SMS.'));
    } finally {
      setSendingSms(false);
    }
  }

  const hasLink = !!linkData;
  const hasEmail = !!patient?.email;
  const hasCell = !!patient?.cellPhone;
  // Transactional opt-out is enforced server-side (422 EMAIL_OPTED_OUT /
  // SMS_OPTED_OUT); mirrored here so the button is dead before the round-trip.
  const emailOptedOut = isEmailOptedOut(patient);
  const smsOptedOut = isSmsOptedOut(patient);
  const canEmail = hasEmail && hasLink && !emailOptedOut;
  const canSms = hasCell && hasLink && !smsOptedOut;

  const emailDisabledReason = !hasLink
    ? 'Choose a form first.'
    : !hasEmail
      ? 'No email on file — add one on the patient record.'
      : emailOptedOut
        ? 'This patient has opted out of transactional emails.'
        : null;
  const smsDisabledReason = !hasLink
    ? 'Choose a form first.'
    : !hasCell
      ? 'No cell phone on file — add one on the patient record.'
      : smsOptedOut
        ? 'This patient has opted out of transactional SMS messages.'
        : null;

  const optOutNotice =
    emailOptedOut && smsOptedOut
      ? 'This patient has opted out of transactional emails and SMS messages. Use the link above instead.'
      : emailOptedOut
        ? 'This patient has opted out of transactional emails.'
        : smsOptedOut
          ? 'This patient has opted out of transactional SMS messages.'
          : null;

  const isSuccessFeedback = !!feedback && /sent|mailed|copied/i.test(feedback);

  const footer = (
    <>
      <Button type="button" variant="secondary" onClick={onClose}>
        Close
      </Button>
      <Button
        type="button"
        variant="primary"
        onClick={handleDisplay}
        disabled={!hasLink || linkLoading}
        title={!hasLink ? 'Choose a form first.' : undefined}
      >
        Display E-Form
      </Button>
    </>
  );

  return (
    <Modal isOpen={open} onClose={onClose} title="E-Forms" size="xl" footer={footer}>
      <div className="space-y-6">
        <Section
          step={1}
          title="Choose an E-Form"
          hint="Pick the form you want the patient to fill out."
        >
          <SearchableCombobox<string>
            mode="single"
            options={comboOptions}
            value={surveyId === '' ? null : surveyId}
            onChange={(v) => handleSurveyChange(v ?? '')}
            loading={surveysLoading}
            placeholder="Select an E-Form..."
            emptyMessage="No matching forms"
            clearable
          />
        </Section>

        {isCustomSurvey && replacementCodes && replacementCodes.length > 0 && (
          <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">Custom fields for this form</h4>
              <p className="mt-0.5 text-xs text-slate-500">
                Values used to personalize this form before sending.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {replacementCodes.map((code) => (
                <div key={code} className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-slate-600">{code}</label>
                  <Input
                    value={customValues[code] ?? ''}
                    onChange={(e) => handleCustomChange(code, e.target.value)}
                    placeholder={`Enter ${code}`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Section
              step={2}
              title="Link to a treatment plan"
              hint="Optional. Attach this form's responses to a specific plan."
            >
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-slate-100 text-sm">
                  <thead className="bg-slate-800 text-white">
                    <tr className="text-left text-xs font-semibold uppercase tracking-wide">
                      <th className="w-10 px-3 py-2" />
                      <th className="px-3 py-2">Plan</th>
                      <th className="px-3 py-2">Last Contact</th>
                      <th className="px-3 py-2">Procedure(s)</th>
                      <th className="px-3 py-2">Stage</th>
                      <th className="px-3 py-2">Quote</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <tr
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handlePlanChange(0)}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="radio"
                          name="eformPlan"
                          value={0}
                          checked={planId === 0}
                          onChange={() => handlePlanChange(0)}
                        />
                      </td>
                      <td className="px-3 py-2 font-semibold text-slate-800">None</td>
                      <td className="px-3 py-2 text-slate-400" colSpan={4}>
                        Don't link this form to a plan
                      </td>
                    </tr>
                    {plansLoading ? (
                      <tr>
                        <td colSpan={6} className="px-3 py-4 text-center">
                          <Spinner />
                        </td>
                      </tr>
                    ) : (
                      (plans ?? []).map((p) => (
                        <tr
                          key={p.planId}
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => handlePlanChange(p.planId)}
                        >
                          <td className="px-3 py-2">
                            <input
                              type="radio"
                              name="eformPlan"
                              value={p.planId}
                              checked={planId === p.planId}
                              onChange={() => handlePlanChange(p.planId)}
                            />
                          </td>
                          <td className="px-3 py-2 text-slate-800">
                            {p.displayId || p.name || `#${p.planId}`}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {formatShortDate(p.lastContact ?? p.date)}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {p.procedures && p.procedures.length > 0 ? p.procedures.join(', ') : 'None'}
                          </td>
                          <td className="px-3 py-2 text-slate-700">{p.stageName ?? ''}</td>
                          <td className="px-3 py-2 text-slate-700">
                            {formatCurrency(p.quoteCost ?? null)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Section>
          </div>

          <div>
            <Section
              step={3}
              title="Send to patient"
              hint="Share this form with the patient or open it yourself."
            >
              <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex h-[210px] items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 p-2">
                  {linkLoading ? (
                    <Spinner />
                  ) : linkData ? (
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(linkData.shortUrl)}`}
                      alt="E-Form QR code — patient can scan to open the form"
                      className="h-[190px] w-[190px]"
                    />
                  ) : (
                    <span className="px-3 text-center text-xs text-slate-500">
                      Pick a form in step 1 to generate a patient link.
                    </span>
                  )}
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-600">Short link</label>
                  <div className="mt-1 flex gap-2">
                    <input
                      readOnly
                      value={linkData?.shortUrl ?? ''}
                      placeholder="—"
                      className="flex-1 min-w-0 truncate rounded border border-slate-300 bg-slate-50 px-2 py-1 text-xs text-slate-700"
                      onFocus={(e) => e.currentTarget.select()}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleCopy}
                      disabled={!hasLink}
                      className="flex items-center gap-1 px-2"
                      title={!hasLink ? 'Choose a form first.' : 'Copy short link'}
                    >
                      {copied ? (
                        <>
                          <Check className="mr-1 h-3.5 w-3.5" />
                          <span className="text-xs">Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="mr-1 h-3.5 w-3.5" />
                          <span className="text-xs">Copy</span>
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {optOutNotice && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    {optOutNotice}
                  </div>
                )}

                <div className="space-y-2">
                  <div>
                    <Button
                      type="button"
                      variant="secondary"
                      className="flex w-full items-center justify-center overflow-hidden text-xs"
                      onClick={handleSendEmail}
                      disabled={!canEmail || sendingEmail}
                      title={emailDisabledReason ?? (hasEmail ? `Email ${patient?.email}` : undefined)}
                    >
                      <Mail className="mr-2 h-4 w-4 flex-shrink-0" />
                      <span className="truncate">
                        {sendingEmail
                          ? 'Sending...'
                          : hasEmail
                            ? `Email ${patient?.email}`
                            : 'Email'}
                      </span>
                    </Button>
                    {emailDisabledReason && (
                      <p className="mt-1 text-[11px] text-slate-500">{emailDisabledReason}</p>
                    )}
                  </div>

                  <div>
                    <Button
                      type="button"
                      variant="secondary"
                      className="flex w-full items-center justify-center overflow-hidden text-xs"
                      onClick={handleSendSms}
                      disabled={!canSms || sendingSms}
                      title={smsDisabledReason ?? (hasCell ? `SMS ${formatPhone(patient?.cellPhone)}` : undefined)}
                    >
                      <MessageSquare className="mr-2 h-4 w-4 flex-shrink-0" />
                      <span className="truncate">
                        {sendingSms
                          ? 'Sending...'
                          : hasCell
                            ? `SMS ${formatPhone(patient?.cellPhone)}`
                            : 'SMS'}
                      </span>
                    </Button>
                    {smsDisabledReason && (
                      <p className="mt-1 text-[11px] text-slate-500">{smsDisabledReason}</p>
                    )}
                  </div>
                </div>

                {linkError && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {linkError}
                  </div>
                )}
                {feedback && (
                  <div
                    className={
                      isSuccessFeedback
                        ? 'rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700'
                        : 'rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700'
                    }
                  >
                    {feedback}
                  </div>
                )}
              </div>
            </Section>
          </div>
        </div>
      </div>
    </Modal>
  );
}
